import FullWidthLayout from "@/layouts/FullWidthLayout";
import KDriversStatusTable from "kinnect-front/KYard/Drivers/KDriversStatusTable";
import KSchedulesTable from "kinnect-front/KYard/Schedule/KSchedulesTable";
import KTripsTable from "kinnect-front/KYard/Trips/KTripsTable";
import { Broadcastable, Kioskable, FetchesData, Pageable } from "kinnect-mixins";
import { KAutoScroll } from "kinnect-general";
import {
	VBtn,
	VCol,
	VRow,
	VSpacer
} from "vuetify/lib";
import { YardDriver, YardSchedule, YardTrip } from "kinnect-models";
import { isSameDay, parseISO } from "date-fns";

export default {
	name: "YardHubPage",

	metaInfo() {
		const title = this.kiosk
			? "Yard - Hub Kiosk"
			: "Yard - Hub";

		return { title };
	},

	mixins: [
		Broadcastable,
		Kioskable,
		FetchesData,
		Pageable
	],

	data: () => ( {
		drivers: [],
		schedules: [],
		trips: [],
		kioskRoute: { name: "yardHubKiosk" },
		broadcasts: [
			{
				Model: YardTrip,
				channel: "trip",
				propKey: "trips",
				eventNames: {
					c: "trip.created",
					u: "trip.updated",
					d: "trip.deleted"
				}
			},
			{
				Model: YardSchedule,
				channel: "schedule",
				propKey: "schedules",
				eventNames: {
					c: "schedule.created",
					u: "schedule.updated",
					d: "schedule.deleted"
				}
			},
			{
				Model: YardDriver,
				channel: "driver",
				propKey: "drivers",
				eventNames: {
					c: "driver.created",
					u: "driver.updated",
					d: "driver.deleted"
				}
			}
		]
	} ),

	computed: {
		filteredTrips() {
			return this.trips.filter( trip => {
				const isActive = !trip.completed;
				const isValidDate = isSameDay(
					new Date(),
					parseISO( trip.deliveryDate )
				);

				return isActive && isValidDate;
			} );
		},

		filteredSchedules() {
			return this.schedules.filter( schedule => {
				const isTripless = !schedule.idTrip;
				const isValidDate = !schedule.deliveryDate
					|| isSameDay(
						new Date(),
						parseISO( schedule.deliveryDate )
					);

				return isTripless && isValidDate;
			} );
		},

		flexStyle() {
			const styles = {};

			if ( this.kiosk ) {
				styles.height = "50%";
				styles.overflow = "hidden";
			}

			return styles;
		},

		genSchedulesTable() {
			const table = <KSchedulesTable
				class="elevation-1 d-flex flex-column"
				dense
				fixedHeader
				hiddenCols={{
					actions: this.kiosk,
					age: this.kiosk,
					deliveryDate: true,
					deliveryTime: true,
					estLoadTime: this.kiosk
				}}
				hideDefaultFooter={this.kiosk}
				items={this.filteredSchedules}
				itemsPerPage={this.kiosk ? -1 : undefined}
				linkAddress={!this.kiosk}
				loading={this.$wait.is( "get schedules" )}
				style="height: 100%"
			>
				<template slot="title">
					Scheduled Deliveries and Pickups

					<VSpacer></VSpacer>

					{!this.kiosk && this.$can( "browse_yard_schedules" ) && <VBtn
						dark
						outlined
						small
						to={{ name: "yardSchedules" }}
					>View Schedules</VBtn>}
				</template>
			</KSchedulesTable>;

			if ( this.kiosk ) {
				return <KAutoScroll scrollTarget=".v-data-table__wrapper">
					{table}
				</KAutoScroll>;
			}

			return table;
		},

		genDriversStatusTable() {
			const table = <KDriversStatusTable
				class="elevation-1 d-flex flex-column"
				dense
				fixedHeader
				hiddenCols={{ actions: this.kiosk }}
				hideDefaultFooter={this.kiosk}
				items={this.drivers}
				itemsPerPage={this.kiosk ? -1 : undefined}
				loading={this.$wait.is( "get drivers" )}
				style="height: 100%"
			>
				<template slot="title">
					Drivers

					<VSpacer></VSpacer>

					{!this.kiosk && this.$can( "browse_yard_drivers" ) && <VBtn
						dark
						outlined
						small
						to={{ name: "yardDrivers" }}
					>View Drivers</VBtn>}
				</template>
			</KDriversStatusTable>;

			if ( this.kiosk ) {
				return <KAutoScroll scrollTarget=".v-data-table__wrapper">
					{table}
				</KAutoScroll>;
			}

			return table;
		},

		genScheduledTripsTable() {
			const table = <KTripsTable
				class="elevation-1 d-flex flex-column"
				dense
				fixedHeader
				hiddenCols={{
					actions: this.kiosk,
					deliveryDate: true,
					cost: true,
					errors: true,
					estDepartureTime: true,
					estReturnTime: true
				}}
				hideDefaultFooter={this.kiosk}
				items={this.filteredTrips}
				itemsPerPage={this.kiosk ? -1 : undefined}
				linkAddress={!this.kiosk}
				loading={this.$wait.is( "get trips" )}
				onTripUpdated={this.updateTrip}
				schedulesTableProps={{ linkAddress: !this.kiosk }}
				style="height: 100%"
			>
				<template slot="title">
					Active and Assigned Trips

					<VSpacer></VSpacer>

					{!this.kiosk && this.$can( "browse_yard_trips" ) && <VBtn
						dark
						outlined
						small
						to={{ name: "yardTrips" }}
					>View Trips</VBtn>}
				</template>
			</KTripsTable>;

			if ( this.kiosk ) {
				return <KAutoScroll scrollTarget=".v-data-table__wrapper">
					{table}
				</KAutoScroll>;
			}

			return table;
		}
	},

	methods: {
		updateTrip( updatedTrip ) {
			const updateIndex = this.trips.findIndex( trip => updatedTrip.id === trip.id );

			if ( updateIndex !== -1 ) {
				this.$set( this.trips, updateIndex, updatedTrip );
			}
		},

		async fetchTrips() {
			this.trips = await YardTrip
			.custom( "yard/hub/trips" )
			.get();
		},

		async fetchSchedules() {
			this.schedules = await YardSchedule
			.custom( "yard/hub/schedules" )
			.get();
		},

		async fetchDrivers() {
			this.drivers = await YardDriver
			.custom( "yard/hub/drivers" )
			.get();
		},

		async fetch() {
			await this.$http.all( [
				this.fetchTrips(),
				this.fetchSchedules(),
				this.fetchDrivers()
			] );
		}
	},

	render() {
		return <FullWidthLayout>
			<VRow class={{ "fill-height overflow-hidden": this.kiosk }}>
				<VCol
					class="d-flex flex-column pt-0"
					cols={12}
					md={9}
					style={this.flexStyle}
				>{this.genSchedulesTable}</VCol>

				<VCol
					class="d-flex flex-column pt-0"
					cols={12}
					md={3}
					style={this.flexStyle}
				>{this.genDriversStatusTable}</VCol>

				<VCol
					class="d-flex flex-column"
					cols={12}
					style={this.flexStyle}
				>{this.genScheduledTripsTable}</VCol>
			</VRow>
		</FullWidthLayout>;
	}
};
